import * as React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';

import SearchCollapseRow from './SearchCollapseRow';
import { TreeNode } from '../../../../../../types/global';
import { observer } from 'mobx-react';
import SearchPipelineModal from './SearchPipelineModal';

const CollapsibleTreeTable = observer(({ data }: { data: TreeNode }) => {
  const [selectedPaths, setSelectedPaths] = React.useState<string[]>([]);
  const [openModal, setOpenModal] = React.useState(false);
  const [modalData, setModalData] = React.useState<any>(null);

  const getAllChildrenPaths = (node: any, path: string[]) => {
    const allChildren: any = [];
    const filteredChildren =
      node?.children?.filter((child: any) => child.name !== '') ?? [];

    if (filteredChildren.length > 0) {
      for (let children of filteredChildren) {
        const copyPath = [...path, children.name];
        const copyJoinedPath = copyPath.join('/');
        allChildren.push(copyJoinedPath);
      }
    }
    if (node.children && node.children.length > 0) {
      for (const child of node.children) {
        const childPath = [...path, child.name];
        allChildren.push(...getAllChildrenPaths(child, childPath));
      }
    }
    return allChildren;
  };

  const handleSelect = (path: string[], node: any) => {
    const joinedPath = path.join('/');

    setSelectedPaths((prev) => {
      const isAlreadySelected = prev.includes(joinedPath);

      let allChildren: any = [];
      if (node?.children && node.children.length > 0) {
        allChildren = getAllChildrenPaths(node, path);
        allChildren = [joinedPath, ...allChildren];
      }

      if (isAlreadySelected) {
        if (allChildren.length > 0) {
          allChildren = allChildren.filter((p: any) => !prev.includes(p));
        }
        const newSet = new Set([...prev, ...allChildren]);
        const final = Array.from(newSet).filter(
          (p) => !p.startsWith(joinedPath)
        );
        if (final.length === 1) {
          return [];
        }

        return final;
      } else {
        const prefixes = path.map((_, i) => path.slice(0, i + 1).join('/'));
        const newSet = new Set([...prev, ...prefixes, ...allChildren]);

        return Array.from(newSet);
      }
    });
  };

  if (!data || !data.children) {
    return null;
  }

  const getAllLeafPaths = (node: any, path: string[] = []): string[] => {
    const filteredChildren =
      node?.children?.filter((child: any) => child.name !== '') ?? [];

    if (filteredChildren.length === 0) {
      return [path.join('/')];
    }

    return filteredChildren.flatMap((child: any) =>
      getAllLeafPaths(child, [...path, child.name])
    );
  };

  const getLeafSelectionStats = (data: any, selectedPaths: string[]) => {
    const allLeafPaths = getAllLeafPaths(data, []);
    const selectedLeafPaths = allLeafPaths.filter((leafPath) =>
      selectedPaths.includes(leafPath)
    );

    return {
      selectedLeafCount: selectedLeafPaths.length,
      totalLeafCount: allLeafPaths.length,
    };
  };

  const { selectedLeafCount, totalLeafCount } = getLeafSelectionStats(
    data,
    selectedPaths
  );

  return (
    <>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ height: '32px' }}>
              <TableCell sx={{ padding: '0px 4px', width: '32px' }} />
              <TableCell sx={{ padding: '2px 8px' }}>Name</TableCell>
              <TableCell sx={{ padding: '2px 8px' }}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody
            sx={{
              '& > tr:last-of-type td': {
                borderBottom: 'none',
              },
            }}
          >
            {data.children.map((node, index) => (
              <SearchCollapseRow
                key={index}
                orgData={data}
                node={node}
                depth={0}
                onSelect={handleSelect}
                selected={selectedPaths}
                stats={{
                  selected: selectedLeafCount,
                  total: totalLeafCount,
                }}
                fullPath={[]}
                handleOpenModal={(data: any) => {
                  setOpenModal(true);
                  setModalData(data);
                }}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <SearchPipelineModal
        open={openModal}
        handleClose={() => {
          setOpenModal(false);
        }}
        data={modalData}
      />
    </>
  );
});
export default CollapsibleTreeTable;
